/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ChannelReference } from '../models/ChannelReference';
import type { ConnectionKey } from '../models/ConnectionKey';
import type { SSEEventType } from '../models/SSEEventType';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class SseService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     * Subscribe to device events over SSE.
     * Starts a Server-Sent Events (SSE) stream for real-time updates on device events.
     * It's an efficient way to receive continuous updates on device connectivity, mode changes, and other relevant events without the need to poll API endpoints.
     * To reveive events for a single device, specify the device connection key with the `ck` query parameter.
     * To receive events for multiple devices, a channel reference can be specified with the `ck` query parameter. This will receive events from all channel subcribers.
     * Optionally filter events by specifying the `events` query parameter. If not specified, all events are pushed to the client.
     * Ex. events=device_connected,device_disconnected
     *
     * **NOTE1:**The Swagger UI does not support SSE, so you can not directly test the endpoint from this page. The endpoint works with any SSE-compatible client, such as a browser or a dedicated SSE client.
     *
     * **NOTE2:**The endpoint requires authentication. Since SSE does not support headers, the credentials must be passed as a query parameter (apikey). You can use an ApplicationID or an issued client token as the apikey value.
     *
     * This Handy v3 OpenAPI specification defines schema definitions for each event type. If you use a code generation tool that generates schemas, you should be able to use these types when parsing the SSE stream events.
     *
     * The SSE stream provides the following events:
     * - `battery_changed` Received when the battery status have changed.
     * - `ble_status_changed` Received when the BLE status have changed.
     * - `button_event` Received in case of an unhandled button event. Eg. the user uses a device button in a way ignored by the current device mode.
     * - `device_clock_synchronized` Received when the device clock have finished synchronization with the server clock.
     * - `device_connected` Received when the device connects.
     * - `device_disconnected` Received when the device disconnects.
     * - `device_error` Received when a device error occurs.
     * - `device_status` Received when starting the SSE connection.
     * - `hamp_state_changed` Received when the HAMP state have changed.
     * - `hrpp_state_changed` Received when the HRPP state have changed.
     * - `hdsp_state_changed` Received when the HDSP state have changed.
     * - `hsp_looping` Received when the HSP starts a new loop.
     * - `hsp_starving` Received when the HSP is starving (no more data to play). Only sent if pause_on_starving is disabled.
     * - `hsp_state_changed` Received when the HSP state have changed.
     * - `hsp_threshold_reached` Received when the HSP data threshold is reached.
     * - `hsp_paused_on_starving` Received when the HSP is paused due to starvation. Only sent if pause_on_starving is enabled.
     * - `hsp_resumed_on_not_starving` Received when the HSP is resumed after starvation and playable data is available. Only sent if pause_on_starving is enabled.
     * - `stream_end_reached` Received when the end of a closed stream have been reached. This includes scripts played with the HSSP protocol or closed streams played with STREAM protocol.
     * - `hvp_state_changed` Received when the HVP state changes.
     * - `low_memory_error` Received when the device failed to handle some command due to memory limitations.
     * - `low_memory_warning` Received when the device's available free memory is critically low.
     * - `mode_changed` Received when the device mode have changed.
     * - `ota_progress` Received when the OTA progress have changed.
     * - `slider_blocked` Received when the slider is blocked.
     * - `slider_unblocked` Received when the slider is unblocked.
     * - `stroke_changed` Received when the stroke region have changed.
     * - `temp_high` Received when the device temperature is high.
     * - `temp_ok` Received when the device temperature is back to normal.
     * - `wifi_scan_complete` Received when a device wifi scan have completed.
     * - `wifi_status_changed` Received when the wifi status have changed.
     *
     * See [Handy v3 API documentation](https://ohdoki.notion.site/Handy-API-v3-ea6c47749f854fbcabcc40c729ea6df4) for more information and code samples.
     * @param xConnectionKey A device connection key or a channel reference.
     * @param timeout The timeout in milliseconds for the operation. If the device does not respond within the set timeout, a device timeout error response is returned.
     * If not specified, a default value of 5000 milliseconds is used. The default value should be more then long enough under normal circumstances.
     * If a device has a very high RTD (round trip delay) or is under heavy load, the timeout value may need to be increased.
     * If you experience timeouts, you can try to increase the timeout value.
     *
     * @param events Optional event filter. If not specified, all events are pushed to the client.
     *
     * @returns string SSE stream of device events
     * @throws ApiError
     */
    public getEvents(
        xConnectionKey: (ConnectionKey | ChannelReference),
        timeout: number = 5000,
        events?: Array<SSEEventType>,
    ): CancelablePromise<string> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/sse',
            headers: {
                'X-Connection-Key': xConnectionKey,
            },
            query: {
                'timeout': timeout,
                'events': events,
            },
        });
    }

}
